import { PageContainer, PageHeader } from '@/components/Atoms'
import { Block } from '@/components/Block'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import {
  useMaterialGroupOverrides,
  useUpdateMaterialGroupOverrides,
} from '@/hooks/queries/crt/useMaterialGroupOverrides'
import { UpdateMaterialGroupOverrides } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import MaterialGroupOverridesDataTable from './-components/MaterialGroupOverridesDataTable'

export const Route = createFileRoute(
  '/_auth/_modules/crt/materials/insights/material-groups-overrides/',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const { t } = useTranslation('crt')

  // sourceCode → targetCode (null clears the override).
  const [edited, setEdited] = useState<Map<string, string | null>>(new Map())

  const { data, isPending, error } = useMaterialGroupOverrides()
  const { mutate: updateOverrides, isPending: isSaving } = useUpdateMaterialGroupOverrides()

  const rows = data?.results ?? []

  const handleValueChange = (sourceCode: string, targetCode: string | null) => {
    setEdited((prev) => {
      const next = new Map(prev)
      const original = rows.find((r) => r.code === sourceCode)?.targetCode ?? null
      if (targetCode === original) {
        next.delete(sourceCode)
      } else {
        next.set(sourceCode, targetCode)
      }
      return next
    })
  }

  // Stage the same target across every selected group; saved together via the Save button below.
  const handleBulkEdit = (sourceCodes: string[], targetCode: string | null) => {
    setEdited((prev) => {
      const next = new Map(prev)
      for (const sourceCode of sourceCodes) {
        const original = rows.find((r) => r.code === sourceCode)?.targetCode ?? null
        if (targetCode === original) {
          next.delete(sourceCode)
        } else {
          next.set(sourceCode, targetCode)
        }
      }
      return next
    })
  }

  const handleSaveChanges = () => {
    if (edited.size === 0) return

    const updates: UpdateMaterialGroupOverrides['updates'] = []
    edited.forEach((targetCode, sourceCode) => {
      updates.push({ sourceCode, targetCode })
    })

    updateOverrides(
      { updates },
      {
        onSuccess: () => {
          setEdited(new Map())
        },
      },
    )
  }

  const areChangesPending = edited.size > 0

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <PageHeader>{t('materialGroupOverrides.pageHeader')}</PageHeader>

      <MaterialGroupOverridesDataTable
        data={rows}
        isPending={isPending}
        error={error}
        edited={edited}
        onValueChange={handleValueChange}
        onBulkEdit={handleBulkEdit}
      />

      <PermissionsGuard required={['crt_material_group_overrides_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
